import React, { useState, useMemo, useEffect } from "react";
import {
    Typography,
    Modal,
    Button,
    Box,
    Container,
    CircularProgress,
    List,
    Grid,
} from "@mui/material/";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import actions from "../Store/actions";
import { format } from "../common/lib";
import makeStyles from "@mui/styles/makeStyles";
import styles from "./styles.js";
import RoomIcon from "@mui/icons-material/Room";
import EventIcon from "@mui/icons-material/Event";
import { useTranslation } from "react-i18next";
import ListDetail from "./ListDetail";
import { ActionBar } from "../common/components";
import { useAudit, useToaster, useAuditProgressDetails } from "../common/hooks";
import DeviceSystemPermissions from "./DeviceSystemPermissions";
import AuditProgress from "./AuditProgress";
import CameraLocationError from "../common/components/CameraLocationError";
import { logToSentry } from "../config/Sentry";

const AuditDetails = () => {
    let navigate = useNavigate();
    const { t } = useTranslation();
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();
    const { auditId } = useParams();

    const audit = useAudit(auditId);
    const toast = useToaster();

    const [loading, setLoading] = useState(false);
    const [messageIsShown, setMessageIsShown] = useState(false);
    const [permissions, setPermissions] = useState(false); // true = all good

    const { audits, auditLocations } = useSelector((state) => state);

    const showMessage = () => setMessageIsShown(true);
    const hideMessage = () => setMessageIsShown(false);

    const getLocations = () => {
        return auditLocations.data
            .filter((item) => item.audit.id === parseInt(auditId))
            .map((item) => item.location);
    };

    const locations = useMemo(getLocations, [auditLocations]);

    const { isComplete, alt, assigned, required } = useAuditProgressDetails(
        "db",
        auditId
    );
    const auditStarted = required - assigned === required ? false : true;
    const singleButtonLayout = auditStarted === true ? true : false;

    useEffect(() => {
        setLoading(true);
        if (!audits.item) dispatch(actions.audits.create.itemSet(audit));
        dispatch(actions.auditCompletion.create.init(auditId));
        dispatch(actions.auditUpload.create.statusInit());
        setLoading(false);
    }, [audits]);

    const createButton = (
        clickFn: Function,
        loading: Boolean,
        title: string,
        variantType: Boolean
    ) => (
        <Grid item xs={singleButtonLayout ? 6 : 12}>
            <Button
                disabled={loading}
                fullWidth
                variant={variantType === false ? "outlined" : "contained"}
                loading={loading.toString()}
                onClick={clickFn}
                className={`${classes.buttons} 
               ${!singleButtonLayout ? classes.smallFontSize : ""}`}
            >
                {title}
            </Button>
        </Grid>
    );

    const onSubmitPress = async () => {
        navigate(`/auth/audits/${auditId}/submit`);
    };

    const onStartContinuePress = async () => {
        const { id } = audit;

        try {
            showMessage();
            setLoading(true);
            await dispatch(actions.geoLocation.create.getRequest());
            navigate(`/auth/audits/${id}/assets/list`);
        } catch (error) {
            console.log(error);
            const link = (
                <CameraLocationError message={t("errors.geoLocationGet")} />
            );
            toast.error(link);

            hideMessage();
            setLoading(false);
            logToSentry("onStartContinuePress", {
                error,
            });
            return;
        }
    };

    if (!audit)
        return (
            <Box className={classes.spinnerContainer}>
                <CircularProgress />
            </Box>
        );

    return (
        <>
            <Container className={classes.auditContainer}>
                {audit && (
                    <>
                        <List className={classes.listContainer}>
                            <ListDetail
                                icon={<RoomIcon color="primary" />}
                                primaryText={audit.branch.name}
                                data={locations}
                            />
                            <ListDetail
                                icon={<EventIcon color="primary" />}
                                primaryText={`${t("due")}: ${format.dateTime(
                                    audit.endDate
                                )}`}
                            />
                        </List>
                        <AuditProgress
                            alt={alt}
                            assigned={assigned}
                            required={required}
                        />
                        <DeviceSystemPermissions
                            setPermissions={setPermissions}
                        />

                        <ActionBar>
                            <Grid container spacing={2}>
                                {auditStarted &&
                                    createButton(
                                        onSubmitPress,
                                        loading,
                                        t(
                                            isComplete
                                                ? "submitAudit"
                                                : "submitAudit" //submitPartialAudit
                                        ),
                                        isComplete
                                    )}
                                {createButton(
                                    onStartContinuePress,
                                    loading || !permissions,
                                    t(
                                        auditStarted
                                            ? "continueAudit"
                                            : "startAudit"
                                    ),
                                    !isComplete
                                )}
                            </Grid>
                        </ActionBar>
                    </>
                )}
                <Modal
                    open={messageIsShown}
                    onClose={hideMessage}
                    aria-labelledby="message-modal"
                    aria-describedby="preparing-audit-modal"
                >
                    <Box className={classes.modalBox}>
                        <Typography
                            id="modal-modal-description"
                            className={classes.progress}
                        >
                            {t("messages.preparingAudit")}
                        </Typography>
                        <CircularProgress />
                    </Box>
                </Modal>
            </Container>
        </>
    );
};

export default AuditDetails;
